/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Dispatch, SetStateAction } from "react";
import { ApolloError } from "@apollo/client";
import { showError, showSuccess } from "~/services/toast";
import { isNotEmpty, Maybe } from "~/tools";

export type Environment = {
  nid: string;
  name: string;
  orgUnitsV2?: Maybe<{
    orgUnits?: Maybe<
      {
        nid: string;
        name: string;
        businessContext: {
          id: string;
        };
      }[]
    >;
  }>;
};

export function splitEnvsByConflict(
  acc: (Maybe<Environment> | undefined)[],
  structureId: string
) {
  return acc.reduce(
    (
      [pass, fail]: (Maybe<Environment> | undefined)[][],
      element: Maybe<Environment> | undefined
    ) => {
      return element?.orgUnitsV2?.orgUnits?.find(
        (ou) => ou.businessContext.id === structureId
      )
        ? [[...pass, element], fail]
        : [pass, [...fail, element]];
    },
    [[], []]
  );
}

export function showUnattachmentToasts(
  errors: [ApolloError, Maybe<Environment> | undefined][],
  envsToRemoveLength: number
) {
  if (envsToRemoveLength - errors.length > 0) {
    showSuccess(
      `Attachment removed for ${
        envsToRemoveLength - errors.length
      } of ${envsToRemoveLength} selected environments`,
      5000
    );
  }
  if (errors.length > 0) {
    showError(
      `Something went wrong for ${errors.length} of ${envsToRemoveLength} removed environments, please try again`,
      {
        autoClose: false,
      }
    );
  }
}

export function filterApps<T extends { name: string }>(
  applications: T[],
  filter: string
) {
  return applications?.filter((application) =>
    isNotEmpty(filter)
      ? application.name
          .toLocaleLowerCase()
          .includes(filter.toLocaleLowerCase())
      : applications
  );
}

type ChangeValueProps = {
  inputEnv: Maybe<Environment>;
  unitId: string;
  envs: Maybe<Environment>[];
  setEnvs: Dispatch<SetStateAction<Maybe<Environment>[]>>;
  envsToRemove: Maybe<Environment>[];
  setEnvsToRemove: Dispatch<SetStateAction<Maybe<Environment>[]>>;
};

export function changeEnvValue({
  inputEnv,
  unitId,
  envs,
  setEnvs,
  envsToRemove,
  setEnvsToRemove,
}: ChangeValueProps) {
  const isCurrentlyAttached =
    inputEnv?.orgUnitsV2?.orgUnits?.find((ou) => ou.nid === unitId) !==
    undefined;
  const envsList = isCurrentlyAttached ? envsToRemove : envs;
  const envIndex = envsList.findIndex((env) => inputEnv?.nid === env?.nid);
  const newEnvs =
    envIndex > -1
      ? envsList.filter((_, index: number) => index !== envIndex)
      : [...envsList, inputEnv];

  if (isCurrentlyAttached) {
    setEnvsToRemove(newEnvs);
  } else {
    setEnvs(newEnvs);
  }
}

type ChangeApplicationValueProps = {
  inputEnvs: Maybe<Environment>[];
  environments: Maybe<Environment>[];
  unitId: string;
  envs: Maybe<Environment>[];
  setEnvs: Dispatch<SetStateAction<Maybe<Environment>[]>>;
  envsToRemove: Maybe<Environment>[];
  setEnvsToRemove: Dispatch<SetStateAction<Maybe<Environment>[]>>;
  countSelected: number;
};

export function changeApplicationValue({
  inputEnvs,
  environments,
  unitId,
  envs,
  setEnvs,
  envsToRemove,
  setEnvsToRemove,
  countSelected,
}: ChangeApplicationValueProps) {
  let newEnvs = [...envs];
  let newEnvsToRemove = [...envsToRemove];

  const isUnifiedValue =
    countSelected === environments?.length || countSelected === 0;

  inputEnvs.forEach((inputEnv) => {
    const isCurrentlyAttached =
      inputEnv?.orgUnitsV2?.orgUnits?.find((ou) => ou.nid === unitId) !==
      undefined;

    if (isCurrentlyAttached) {
      newEnvsToRemove =
        unattachEnvironment(inputEnv, newEnvsToRemove, isUnifiedValue) ?? [];
    } else {
      newEnvs = attachEnvironment(inputEnv, newEnvs, isUnifiedValue) ?? [];
    }
  });

  setEnvs(newEnvs);
  setEnvsToRemove(newEnvsToRemove);
}

const unattachEnvironment = (
  inputEnv: Maybe<Environment>,
  envsToRemove: Maybe<Environment>[],
  unifiedValue: boolean
) => {
  const envIndex = envsToRemove.findIndex((env) => inputEnv?.nid === env?.nid);
  if (envIndex > -1) {
    return envsToRemove.filter((_, index: number) => index !== envIndex);
  }
  if (envIndex === -1 && unifiedValue) {
    return [...envsToRemove, inputEnv];
  }
};

const attachEnvironment = (
  inputEnv: Maybe<Environment>,
  envs: Maybe<Environment>[],
  unifiedValue: boolean
) => {
  const envIndex = envs.findIndex((env) => inputEnv?.nid === env?.nid);
  if (envIndex > -1 && unifiedValue) {
    return envs.filter((_, index: number) => index !== envIndex);
  }
  if (envIndex === -1) {
    return [...envs, inputEnv];
  }
};
